<script lang="ts">
  import type { Snippet } from "svelte"
  import type { CardImportantNumber } from "../../../../core/layout/card"
  import { tooltip } from "../../../../core/overlay/tooltip"
  import type {
    WorkflowSummaryMetric,
    WorkflowSummaryModel,
    WorkflowSummarySurface,
    WorkflowSummaryTone,
  } from "../../../../core/layout/workflowSummary"
  import { workflowSummaryToneClass } from "../../../../core/layout/workflowSummary"
  import Card from "../card/index"

  type Props = {
    model: WorkflowSummaryModel
    surface?: WorkflowSummarySurface
    /** @deprecated Use surface="dark" or surface="light". */
    darkMode?: boolean
    compact?: boolean
    footerSnippet?: Snippet | null
  }

  let {
    model,
    surface = undefined,
    darkMode = undefined,
    compact = false,
    footerSnippet = null,
  }: Props = $props()

  const resolvedSurface = $derived<WorkflowSummarySurface>(
    surface ?? (darkMode === false ? "light" : "dark"),
  )
  const tone = (value: WorkflowSummaryTone | undefined) => workflowSummaryToneClass(value)
  const metricCardClasses = (_metric: WorkflowSummaryMetric) => "workflow-summary-card"
  const metricImportantNumbers = (metric: WorkflowSummaryMetric): CardImportantNumber[] =>
    (metric.facts ?? []).map((fact) => ({
      key: `${metric.id}-${fact.label}`,
      label: fact.label,
      value: fact.value,
    }))
</script>

<section
  class="workflow-summary"
  class:dark={resolvedSurface === "dark"}
  class:light={resolvedSurface === "light"}
  class:compact
>
  <header class="summary-header">
    {#if model.eyebrow}
      <p class="eyebrow">{model.eyebrow}</p>
    {/if}
    <h3>{model.title}</h3>
    {#if model.description}
      <p class="description">{model.description}</p>
    {/if}
  </header>

  <div class="metric-grid">
    {#each model.metrics as metric (metric.id)}
      <Card
        brightBackground={resolvedSurface === "light"}
        classes={metricCardClasses(metric)}
        importantNumbers={metricImportantNumbers(metric)}
      >
        <div class="metric-content">
          <div class="metric-heading">
            <span class="metric-label">{metric.label}</span>
            {#if metric.tooltipContent}
              <span
                class="metric-tooltip-trigger"
                aria-label="Hinweis"
                use:tooltip={{ content: metric.tooltipContent }}
              >
                i
              </span>
            {/if}
          </div>
          <strong>{metric.value}</strong>
          {#if metric.subLabel}
            <span class="metric-sub-label">{metric.subLabel}</span>
          {/if}
          {#if metric.description}
            <p>{metric.description}</p>
          {/if}
        </div>
      </Card>
    {/each}
  </div>

  {#if model.messages?.length}
    <div class="messages">
      {#each model.messages as message (message.id)}
        <article class="message message-{tone(message.tone)}">
          <strong>{message.title}</strong>
          {#if message.body}
            <p>{message.body}</p>
          {/if}
        </article>
      {/each}
    </div>
  {/if}

  {#if model.nextSteps?.length}
    <section class="next-steps">
      <h4>Noch zu tun</h4>
      <ul>
        {#each model.nextSteps as step (step.id)}
          <li class="next-step next-step-{tone(step.tone)}">{step.label}</li>
        {/each}
      </ul>
    </section>
  {/if}

  {#if footerSnippet}
    <footer>
      {@render footerSnippet()}
    </footer>
  {/if}
</section>

<style lang="less">
  .workflow-summary {
    --summary-border: rgba(255, 255, 255, 0.16);
    --summary-fact-border: rgba(255, 255, 255, 0.08);
    --summary-fact-surface: rgba(255, 255, 255, 0.04);
    --summary-surface: color-mix(in srgb, #111 92%, var(--text-100, #f8f2dd) 8%);
    --summary-text: var(--text-100, #f8f2dd);
    --summary-muted: color-mix(in srgb, var(--text-100, #f8f2dd) 68%, transparent);
    --summary-card-text: var(--text-100, #f8f2dd);
    --summary-card-muted: color-mix(in srgb, var(--text-100, #f8f2dd) 62%, transparent);
    color: var(--summary-text);
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;

    &.light {
      --summary-border: color-mix(in srgb, var(--text-invers-100, #182734) 14%, transparent);
      --summary-fact-border: color-mix(in srgb, var(--text-invers-100, #182734) 8%, transparent);
      --summary-fact-surface: color-mix(in srgb, var(--text-invers-100, #182734) 5%, transparent);
      --summary-surface: #fff;
      --summary-text: var(--text-invers-100, #182734);
      --summary-muted: color-mix(in srgb, var(--text-invers-100, #182734) 64%, transparent);
      --summary-card-text: var(--text-invers-100, #182734);
      --summary-card-muted: color-mix(in srgb, var(--text-invers-100, #182734) 64%, transparent);
    }

    &.compact {
      gap: 0.75rem;
    }
  }

  .summary-header {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .eyebrow,
  .description,
  h3,
  h4,
  p {
    margin: 0;
  }

  .eyebrow {
    color: var(--summary-muted);
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
  }

  h3 {
    font-size: 1.45rem;
    line-height: 1.15;
  }

  .description {
    color: var(--summary-muted);
    line-height: 1.35;
  }

  .metric-grid {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
  }

  .message {
    background: var(--summary-surface);
    border: 1px solid var(--summary-border);
    border-radius: 6px;
    color: var(--summary-card-text);
  }

  .metric-heading {
    align-items: center;
    display: flex;
    gap: 0.4rem;
    justify-content: space-between;
    min-height: 1rem;
  }

  .metric-label {
    color: var(--summary-card-muted);
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
  }

  .metric-tooltip-trigger {
    align-items: center;
    border: 1px solid currentColor;
    border-radius: 999px;
    color: var(--summary-card-muted);
    display: inline-flex;
    font-size: 0.65rem;
    font-weight: 800;
    height: 1rem;
    justify-content: center;
    line-height: 1;
    width: 1rem;
  }

  .metric-content {
    align-items: flex-start;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.42rem;
    min-height: 6.1rem;
    position: relative;
    z-index: 1;
  }

  .metric-content strong {
    font-size: clamp(1.35rem, 2.1vw, 1.85rem);
    line-height: 1;
    overflow-wrap: anywhere;
  }

  .metric-sub-label,
  .metric-content p,
  .message p {
    color: var(--summary-card-muted);
    font-size: 0.9rem;
    line-height: 1.35;
  }

  .messages {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .message {
    border-left-width: 4px;
    padding: 0.75rem 0.9rem;
  }

  .message strong {
    display: block;
  }

  .next-steps {
    border-bottom: 1px solid var(--summary-border);
    border-top: 1px solid var(--summary-border);
    color: var(--summary-card-text);
    padding: 0.75rem 0;
  }

  .next-steps h4 {
    color: var(--summary-card-muted);
    font-size: 0.72rem;
    text-transform: uppercase;
  }

  ul {
    display: grid;
    gap: 0.25rem;
    list-style: none;
    margin: 0.45rem 0 0;
    padding: 0;
  }

  .next-step {
    color: var(--summary-card-text);
    line-height: 1.35;
    padding: 0.1rem 0 0.1rem 0.85rem;
    position: relative;
  }

  .next-step::before {
    background: currentColor;
    content: "";
    height: 0.28rem;
    left: 0.12rem;
    opacity: 0.72;
    position: absolute;
    top: 0.64em;
    width: 0.28rem;
  }

  footer {
    display: flex;
    justify-content: flex-end;
  }

  .message-success {
    border-color: var(--signal-green-70, #16a66a);
  }

  .message-info {
    border-color: var(--signal-blue-70, #2f66ff);
  }

  .message-warning {
    border-color: var(--signal-orange-70, #f59e0b);
  }

  .message-danger {
    border-color: var(--signal-red-70, #d84343);
  }

  .message-neutral {
    border-color: var(--summary-border);
  }

  :global(.workflow-summary-card) {
    min-height: 8.8rem;
  }

  :global(.workflow-summary-card .card-content) {
    color: var(--summary-card-text);
  }

  .light :global(.workflow-summary-card.brightBackground .card-content) {
    color: var(--text-invers-100, #182734);
  }

  .light :global(.workflow-summary-card.brightBackground .card-footer__label) {
    color: color-mix(in srgb, var(--text-invers-100, #182734) 72%, transparent);
  }

  .light :global(.workflow-summary-card.brightBackground .card-footer__value) {
    color: var(--text-invers-100, #182734);
  }
</style>
